import { ReactNode, memo, useMemo } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiChip } from "@miplatform/components";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { formatDate, isToday } from "@miplatform/utils";
import { Box, SxProps, Typography } from "@mui/material";
import {
  ASSIGNMENTS_CALENDAR_NO_ASSIGNMENTS_MESSAGE,
  ASSIGNMENTS_CALENDAR_NO_ORDERS_MESSAGE,
} from "./AssignmentsCalendar.constants";

interface AssignmentsColumnSectionProps {
  isOrdersColumn?: boolean;
  date?: string | Moment;
  count?: number;
  height?: number | string;
  children?: ReactNode;
  sx?: SxProps;
}

export const AssignmentsColumnSection = memo(
  ({
    isOrdersColumn,
    date,
    count,
    height = "100%",
    children,
    sx,
  }: AssignmentsColumnSectionProps) => {
    const { theme, classes } = useStyles();
    const { formattedDate, dayOfWeek } = useMemo(() => {
      if (!date) {
        return { formattedDate: "", dayOfWeek: "" };
      }

      const formattedDate = formatDate(date, DATE_TIME_FORMATS.FORMAT_MM_DD_YY);
      const dayOfWeek = isToday(date)
        ? "Today"
        : formatDate(date, DATE_TIME_FORMATS.FORMAT_DDD);

      return { formattedDate, dayOfWeek };
    }, [date]);

    return (
      <Box className={classes.root} height={height} sx={sx}>
        {date && (
          <Box className={classes.header}>
            <MiChip
              label={count}
              size="small"
              color="grey"
              sx={{
                height: 14,
                fontSize: 11,
                backgroundColor: theme.palette.grey.medium,
              }}
            />
            <Typography className={classes.subTitle} flex={1}>
              {dayOfWeek}
            </Typography>
            <Typography className={classes.subTitle}>
              {formattedDate}
            </Typography>
          </Box>
        )}
        <Box className={classes.wrapper}>
          <Box
            className={classes.content}
            sx={{
              ...(!children && {
                justifyContent: "center",
                alignItems: "center",
              }),
            }}
          >
            {children || (
              <Typography className={classes.subTitle}>
                {isOrdersColumn
                  ? ASSIGNMENTS_CALENDAR_NO_ORDERS_MESSAGE
                  : ASSIGNMENTS_CALENDAR_NO_ASSIGNMENTS_MESSAGE}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  header: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey.darkAccent}`,
    gap: theme.spacing(2.5),
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey.lightAccent,
    borderTopLeftRadius: theme.spacing(1),
    borderTopRightRadius: theme.spacing(1),
    flexShrink: 0,
  },
  wrapper: {
    position: "relative",
    flex: 1,
    width: "100%",
  },
  subTitle: {
    fontSize: theme.fontSize.small,
    fontWeight: theme.fontWeight.semiBold,
  },
  content: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    overflowY: "auto",
    alignItems: "center",
    backgroundColor: theme.palette.blue.light,
  },
}));
